import {
    CountyFilter,
    FilterClear,
    FilterGroup,
    HeaderFilter,
    OptionType,
    RoleFilter,
    useGetFilterPreferencesQuery,
    useOutsideClick,
    useUpdateFilterPreferences,
} from "modules/common";
import { RegistryFilter } from "modules/common/components/CategoryFilter";
import { SettlementFilter } from "modules/common/components/SettlementFilter/SettlementFilter";
import { SendNotificationFilter, mapToFilterName } from "modules/common/utils/filters";
import React, { useEffect, useRef, useState } from "react";
import { UpdateFilterPreferencesRequest } from "services";

interface Props {
    registryFilter: string[];
    setRegistryFilter: React.Dispatch<React.SetStateAction<string[]>>;
    roleFilter: number[];
    setRoleFilter: (value: React.SetStateAction<number[]>) => void;
    countyFilter: number[];
    setCountyFilter: React.Dispatch<React.SetStateAction<number[]>>;
    settlementFilter: number[];
    setCityFilter: React.Dispatch<React.SetStateAction<number[]>>;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    counties?: OptionType<number | null>[];
    isLoadingCounties: boolean;
}

export const SendNotificationFilters: React.FC<Props> = ({
    registryFilter,
    setRegistryFilter,
    roleFilter,
    setRoleFilter,
    countyFilter,
    setCountyFilter,
    settlementFilter,
    setCityFilter,
    setCurrentPage,
    counties,
    isLoadingCounties,
}) => {
    // dropdown states
    const [showRegistryFilter, setShowRegistryFilter] = useState(false);
    const [showRoleFilter, setShowRoleFilter] = useState(false);
    const [showCountyFilter, setShowCountyFilter] = useState(false);
    const [showSettlementFilter, setShowMunicipalityFilter] = useState(false);

    // refs
    const registryFilterRef = useRef<HTMLDivElement>(null);
    const roleFilterRef = useRef<HTMLDivElement>(null);
    const countyFilterRef = useRef<HTMLDivElement>(null);
    const cityFilterRef = useRef<HTMLDivElement>(null);

    // outside click
    useOutsideClick(registryFilterRef, () => setShowRegistryFilter(false));
    useOutsideClick(roleFilterRef, () => setShowRoleFilter(false));
    useOutsideClick(countyFilterRef, () => setShowCountyFilter(false));
    useOutsideClick(cityFilterRef, () => setShowMunicipalityFilter(false));

    const filterPreferencesQuery = useGetFilterPreferencesQuery([
        REGISTRY_FILTER_NAME,
        ROLE_FILTER_NAME,
        COUNTY_FILTER_NAME,
        SETTLEMENT_FILTER_NAME,
    ]);

    // query
    const updateFilterPreferencesMutation = useUpdateFilterPreferences();

    useEffect(() => {
        if (filterPreferencesQuery.some(s => s.isLoading)) return;

        const registryFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === REGISTRY_FILTER_NAME
        )?.data?.filterStringValues;

        const roleFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === ROLE_FILTER_NAME
        )?.data?.filterIntValues;

        const countyFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === COUNTY_FILTER_NAME
        )?.data?.filterIntValues;

        const municipalityFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === SETTLEMENT_FILTER_NAME
        )?.data?.filterIntValues;

        if (registryFilterData) setRegistryFilter(registryFilterData);

        if (roleFilterData) setRoleFilter(roleFilterData);

        if (countyFilterData) setCountyFilter(countyFilterData);

        if (municipalityFilterData) setCityFilter(municipalityFilterData);
    }, [filterPreferencesQuery]);

    // handle filter change based on filter type
    const handleUpdateFilterPreferences = (
        filterName: string,
        filterIntValue?: number,
        filterStringValue?: string,
        filterBoolValue?: boolean,
        clearFilter?: boolean
    ) => {
        const payload: UpdateFilterPreferencesRequest = {
            filterName,
            ...(filterIntValue !== undefined && { filterIntValue }),
            ...(filterStringValue !== undefined && { filterStringValue }),
            ...(filterBoolValue !== undefined && { filterBoolValue }),
            ...(clearFilter !== undefined && { clearFilter }),
        };
        updateFilterPreferencesMutation.mutate(payload);
        setCurrentPage(1);
    };

    // handle filter change
    const handleRegistryFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        handleUpdateFilterPreferences(REGISTRY_FILTER_NAME, undefined, value);
    };
    const handleRoleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const valueNumb = parseInt(value);
        handleUpdateFilterPreferences(ROLE_FILTER_NAME, valueNumb);
    };
    const handleCountyFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const valueNumb = parseInt(value);
        handleUpdateFilterPreferences(COUNTY_FILTER_NAME, valueNumb);
        handleUpdateFilterPreferences(
            SETTLEMENT_FILTER_NAME,
            undefined,
            undefined,
            undefined,
            true
        );
    };
    const onSettlementChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        handleUpdateFilterPreferences(SETTLEMENT_FILTER_NAME, parseInt(event.target.value));

    // handle filter clear
    const handleRegistryFilterClear = () =>
        handleUpdateFilterPreferences(REGISTRY_FILTER_NAME, undefined, undefined, undefined, true);
    const handleRoleFilterClear = () =>
        handleUpdateFilterPreferences(ROLE_FILTER_NAME, undefined, undefined, undefined, true);
    const handleCountyFilterClear = () => {
        handleUpdateFilterPreferences(COUNTY_FILTER_NAME, undefined, undefined, undefined, true);
        handleUpdateFilterPreferences(
            SETTLEMENT_FILTER_NAME,
            undefined,
            undefined,
            undefined,
            true
        );
    };
    const handleSettlementFilterClear = () =>
        handleUpdateFilterPreferences(
            SETTLEMENT_FILTER_NAME,
            undefined,
            undefined,
            undefined,
            true
        );
    const handleClearFilter = () => {
        handleRegistryFilterClear();
        handleRoleFilterClear();
        handleCountyFilterClear();
    };

    // handle click
    const handleRegistryFilterClick = () => setShowRegistryFilter(prev => !prev);
    const handleRoleFilterClick = () => setShowRoleFilter(prev => !prev);
    const handleCountyFilterClick = () => setShowCountyFilter(prev => !prev);
    const handleSettlementFilterClick = () => setShowMunicipalityFilter(prev => !prev);

    return (
        <HeaderFilter name="send-notification">
            <FilterGroup>
                <div ref={registryFilterRef}>
                    <RegistryFilter
                        onChange={handleRegistryFilterChange}
                        onClear={handleRegistryFilterClear}
                        onClick={handleRegistryFilterClick}
                        value={registryFilter}
                        isActive={Boolean(registryFilter.length)}
                        showDropdown={showRegistryFilter}
                    />
                </div>

                <div ref={roleFilterRef}>
                    <RoleFilter
                        onChange={handleRoleFilterChange}
                        onClear={handleRoleFilterClear}
                        onClick={handleRoleFilterClick}
                        value={roleFilter}
                        isActive={Boolean(roleFilter.length)}
                        showDropdown={showRoleFilter}
                    />
                </div>

                <div ref={countyFilterRef}>
                    <CountyFilter
                        onChange={handleCountyFilterChange}
                        onClear={handleCountyFilterClear}
                        onClick={handleCountyFilterClick}
                        value={countyFilter}
                        isActive={Boolean(countyFilter.length)}
                        showDropdown={showCountyFilter}
                        isEnum={false}
                        counties={counties}
                        isLoadingCounties={isLoadingCounties}
                    />
                </div>

                <div ref={cityFilterRef}>
                    <SettlementFilter
                        onChange={onSettlementChange}
                        showDropdown={showSettlementFilter}
                        onClear={handleSettlementFilterClear}
                        onClick={handleSettlementFilterClick}
                        isActive={Boolean(settlementFilter.length)}
                        value={settlementFilter}
                        countyFilter={countyFilter}
                    />
                </div>

                <FilterClear
                    show={Boolean(
                        registryFilter.length ||
                            roleFilter.length ||
                            countyFilter.length ||
                            settlementFilter.length
                    )}
                    onClearFilter={handleClearFilter}
                />
            </FilterGroup>
        </HeaderFilter>
    );
};

// map filter names
const REGISTRY_FILTER_NAME = mapToFilterName<SendNotificationFilter>(
    SendNotificationFilters.name,
    "registryFilter"
);
const ROLE_FILTER_NAME = mapToFilterName<SendNotificationFilter>(
    SendNotificationFilters.name,
    "roleFilter"
);
const COUNTY_FILTER_NAME = mapToFilterName<SendNotificationFilter>(
    SendNotificationFilters.name,
    "countyFilter"
);
const SETTLEMENT_FILTER_NAME = mapToFilterName<SendNotificationFilter>(
    SendNotificationFilters.name,
    "settlementsFilter"
);
